import React, { useState } from "react";
import "./signup.css";
import classNames from "classnames";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import { useMutation } from "@tanstack/react-query";
import { registerUser } from "../../services/authService";
import IndustrySelector from "./IndustrySelector";
import "../LandingPage/pricing.css";

const industryOptions = {
  "Financials": [
    "Retail Banking",
    "Investment Banking",
    "Asset Management",
    "Private Equity",
    "Venture Capital",
    "FinTech",
    "Wealth Management",
    "Credit Unions",
    "Payment Processing",
    "Mortgage & Lending"
  ],
  "Health Care": [
    "Hospitals & Clinics",
    "Medical Devices",
    "Pharmaceuticals",
    "Biotechnology",
    "Health Insurance",
    "Telemedicine",
    "Mental Health Services",
    "Diagnostic Laboratories",
    "Home Health Care",
    "Rehabilitation Centers"
  ],
  "Information Technology": [
    "Software Development",
    "Hardware Manufacturing",
    "IT Services & Consulting",
    "Cloud Computing",
    "Cybersecurity",
    "Data Analytics",
    "Artificial Intelligence",
    "Blockchain",
    "Networking Equipment",
    "SaaS/PaaS/IaaS Platforms"
  ],
  "Real Estate": [
    "Residential Real Estate",
    "Commercial Real Estate",
    "Real Estate Investment Trusts (REITs)",
    "Property Management",
    "Real Estate Development",
    "Brokerage Services",
    "Vacation Rentals",
    "Appraisal Services",
    "Title Insurance",
    "Construction Management"
  ],
  "Transportation": [
    "Freight & Logistics",
    "Rail Transport",
    "Air Transport",
    "Maritime Shipping",
    "Public Transit",
    "Ride-Sharing Services",
    "Courier & Delivery Services",
    "Trucking",
    "Warehousing",
    "Automotive Transport"
  ],
  "Retail": [
    "E-Commerce",
    "Brick-and-Mortar Stores",
    "Apparel & Accessories",
    "Consumer Electronics",
    "Grocery & Supermarkets",
    "Furniture & Home Goods",
    "Beauty & Personal Care",
    "Luxury Goods",
    "Sporting Goods",
    "Discount Stores"
  ],
  "Education": [
    "K-12 Schools",
    "Higher Education (Colleges & Universities)",
    "Vocational & Trade Schools",
    "Online Learning Platforms",
    "Test Preparation",
    "Educational Content & Publishing",
    "Corporate Training",
    "EdTech",
    "Language Learning",
    "Special Education Services"
  ],
  "Hospitality": [
    "Hotels & Resorts",
    "Hostels & BnBs",
    "Event Management",
    "Catering Services",
    "Theme Parks & Attractions",
    "Casinos",
    "Timeshares",
    "Hospitality Consulting",
    "Concierge Services",
    "Convention Centers"
  ],
  "Travel & Leisure": [
    "Travel Agencies",
    "Tour Operators",
    "Airlines",
    "Cruise Lines",
    "Car Rentals",
    "Adventure Tourism",
    "Cultural & Historical Tourism",
    "Travel Insurance",
    "Vacation Rentals",
    "Destination Management Companies (DMCs)"
  ],
  "Food & Beverage": [
    "Restaurants",
    "Food Delivery Services",
    "Packaged Foods",
    "Beverage Production (Alcoholic & Non-alcoholic)",
    "Food Manufacturing",
    "Catering",
    "Food Trucks",
    "Grocery Chains",
    "Bakeries & Confectionery",
    "Organic & Health Foods"
  ],
  "Pharmaceuticals": [
    "Drug Manufacturing",
    "Generic Drugs",
    "Biotechnology Drugs",
    "Contract Research Organizations (CROs)",
    "Vaccine Manufacturers",
    "Nutraceuticals",
    "Clinical Trials",
    "Pharmacy Chains",
    "API Manufacturers",
    "Specialty Pharma"
  ],
  "Insurance": [
    "Life Insurance",
    "Health Insurance",
    "Property & Casualty Insurance",
    "Auto Insurance",
    "Reinsurance",
    "Travel Insurance",
    "Liability Insurance",
    "Insurance Brokers & Agents",
    "InsurTech",
    "Underwriting Services"
  ],
  "Construction": [
    "Residential Construction",
    "Commercial Construction",
    "Industrial Construction",
    "Civil Engineering",
    "General Contracting",
    "Architecture & Design",
    "Construction Equipment Rental",
    "Green Building",
    "Project Management",
    "Building Materials Manufacturing"
  ],
  "Agriculture": [
    "Crop Production",
    "Livestock Farming",
    "Agricultural Equipment",
    "Agrochemicals (Fertilizers, Pesticides)",
    "Organic Farming",
    "Aquaculture",
    "Agribusiness Consulting",
    "Food Processing",
    "Farm-to-Table Supply Chains",
    "AgriTech"
  ],
  "Government": [
    "Public Administration",
    "Defense & Military",
    "Law Enforcement",
    "Public Health Services",
    "Urban Planning",
    "Public Transportation",
    "Education Departments",
    "Environmental Agencies",
    "Emergency Services",
    "Regulatory Bodies"
  ],
  "Nonprofit": [
    "Charities",
    "Foundations",
    "Religious Organizations",
    "Advocacy & Human Rights",
    "Environmental Nonprofits",
    "Educational Nonprofits",
    "Health-related Nonprofits",
    "International NGOs",
    "Arts & Culture Organizations",
    "Social Services"
  ]
}

const pricingPlans = [
  {
    name: "Basic",
    monthlyPrice: 99,
    annualPrice: Math.round((99 * 12 * 0.75) / 12),
    badge: null,
  },
  {
    name: "Pro",
    monthlyPrice: 199,
    annualPrice: Math.round((199 * 12 * 0.75) / 12),
    badge: "Recommended",
  },
  {
    name: "Enterprise",
    monthlyPrice: 399,
    annualPrice: Math.round((399 * 12 * 0.75) / 12),
    badge: null,
  },
];

const NewSignUp = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState("signup");
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    newsletter: true,
    plan: ""
  });
  const [companyFormData, setCompanyFormData] = useState({
    name: "",
    industry: "",
    sector: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [companyErrors, setCompanyErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
    setErrors({ ...errors, [name]: "" });
  };

  const handleCompanyInputChange = (e) => {
    const { name, value } = e.target;
    setCompanyFormData({ ...companyFormData, [name]: value });
    setCompanyErrors({ ...companyErrors, [name]: "" });
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) newErrors.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = "Invalid email format.";
    else if (formData.email.endsWith("@gmail.com"))
      newErrors.email = "Please use a work email (no @gmail.com).";
    if (!formData.password) newErrors.password = "Password is required.";
    else if (formData.password.length < 8)
      newErrors.password = "Password must be 8+ characters.";
    else if (!/[0-9]/.test(formData.password))
      newErrors.password = "Password must contain at least one number.";
    else if (!/[!@#$%^&*]/.test(formData.password))
      newErrors.password = "Password must contain at least one special character.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateCompanyForm = () => {
    const newErrors = {};
    if (!companyFormData.name) newErrors.name = "Name is required.";
    if (!companyFormData.industry) newErrors.industry = "Industry is required.";
    if (!companyFormData.sector) newErrors.sector = "Sector is required.";
    setCompanyErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const registerUserMutation = useMutation({
    mutationFn: registerUser,
    onMutate: () => {
      setIsLoading(true)
    },
    onSuccess: (response) => {
      localStorage.setItem("accessToken", response.data.access_token);
      localStorage.setItem("refreshToken", response.data.refresh_token);
      if (response.data.access_token && response.data.refresh_token)
        navigate('/home')
      setIsLoading(false)
    },
    onError: (error) => {
      setIsLoading(false)
      const err =
        ((error.response.data?.email && error.response.data?.email?.[0]) || (error.response.data?.name && error.response.data?.name?.[0])) ??
        "Registration failed. Please try again.";
      if (err == 'user with this email already exists.') {
        message.error("Email already in use. Forgot your password?");
        setStep('signup')
        return;
      };
      if (err == 'company with this name already exists.') {
        setStep('profile');
      }
      message.error(err);
    },
  });

  const handleNext = (e) => {
    e.preventDefault();
    if (validateForm()) setStep('profile');
  };

  const handleBack = () => {
    if (step === "profile") setStep("signup");
    else if (step === "pricing") setStep("integration");
    else if (step === "integration") setStep("profile");
  };

  const handlePlanClick = (plan, billing) => {
    if (!plan) {
      message.error("Please select a plan.");
      return;
    }
    registerUserMutation.mutate({ company: { ...companyFormData }, ...formData, billingCycle: billing, plan: 'starter' });

  }

  const handleIntegrationNext = () => setStep("pricing");
  const handleFinish = (e) => {
    e.preventDefault();
    if (!validateCompanyForm()) return;
    setStep("integration");
  }

  const handleIndustryChange = (value) => {
    setCompanyFormData({ ...companyFormData, industry: value })
    setCompanyErrors({ ...companyErrors, industry: "" })
  }

  return (
    <div className="signup-page">
      <div className="container-signup">
        <div className="container-signup">
          {step !== "signup" && (
            <button className="back-button" onClick={handleBack}>
              ← Back
            </button>
          )}

          {step === "signup" && (
            <div className="signup-container-signup">
              <h2>The Ultimate Business Awareness Tool</h2>
              <p>Signup for Free</p>
              <form className="signup-form" onSubmit={handleNext}>
                <div className="form-group">
                  <input
                    type="email"
                    name="email"
                    placeholder="Work Email (e.g., name@company.com)"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                  {errors.email && (
                    <span className="error">{errors.email}</span>
                  )}
                </div>
                <div className="form-group password-group">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Password (8+ characters)"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                  />
                  <span className="eye-icon" onClick={togglePasswordVisibility}>
                    {showPassword ? "👁️" : "👁️‍🗨️"}
                  </span>
                  {errors.password && (
                    <span className="error">{errors.password}</span>
                  )}
                </div>
                <label>
                  <input
                    type="checkbox"
                    name="newsletter"
                    checked={formData.newsletter}
                    onChange={handleInputChange}
                  />
                  Sign Up for Weekly Insight Newsletter
                </label>
                <button type="submit">Next</button>
              </form>
              <p className="switch-link">
                Already have an account?{" "}
                <span onClick={() => navigate("/login")}>
                  Sign in
                </span>
              </p>
              <p className="join-text">Join 150+ Others</p>
              <div className="testimonials-section">
                <div className="testimonials-ticker">
                  <div className="ticker-content">
                    <p>“Avoided $50K+ and closed 27% more deals”</p>
                    <p>
                      “32% boost in retention after launching twice as fast”
                    </p>
                    <p>“Saved $75K and boosted margins 22%”</p>
                    <p>“Saved $500K on rent and 18% more profitable”</p>
                    {/* Duplicate for smooth looping */}
                    <p>“Avoided $50K+ and closed 27% more deals”</p>
                    <p>
                      “32% boost in retention after launching twice as fast”
                    </p>
                    <p>“Saved $75K and boosted margins 22%”</p>
                    <p>“Saved $500K on rent and 18% more profitable”</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!isLoading ? step === "pricing" &&  (
            <div className="pricing-overlay">
              <h2>Pick Your Plan—Free 14-Day Trial</h2>
              {/* <div className="pricing-options">
                <div
                  className={classNames("plan", {
                    ["highlighted"]: formData.plan === "starter",
                  })}
                  onClick={() => handlePlanClick('starter')}
                >
                  <h3>Starter</h3>
                  <p>$49/month · Small SMBs (10-50 employees)</p>
                  {formData.plan === "starter" && <span>Selected</span>}
                </div>
                <div
                  className={classNames("plan", {
                    ["highlighted"]: formData.plan === "professional",
                  })}
                  onClick={() => handlePlanClick('professional')}
                >
                  <h3>Professional</h3>
                  <p>$99/month · Mid-tier SMBs (50-200 employees)</p>
                  {formData.plan === "professional" && (
                    <span>Most Popular</span>
                  )}
                </div>
                <div
                  className={classNames("plan", {
                    ["highlighted"]: formData.plan === "enterprise",
                  })}
                  onClick={() => handlePlanClick('enterprise')}
                >
                  <h3>Enterprise</h3>
                  <p>$199/month · Larger SMBs (200-500 employees)</p>
                  {formData.plan === "enterprise" && (
                    <span>Selected</span>
                  )}
                </div>
              </div> */}
              <div className="pricing-cards">
                <div className="billing-toggle">
                  <span className={billingCycle === "monthly" ? "active" : ""}>
                    Monthly
                  </span>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={billingCycle === "annual"}
                      onChange={() =>
                        setBillingCycle(
                          billingCycle === "monthly" ? "annual" : "monthly"
                        )
                      }
                    />
                    <span className="slider"></span>
                  </label>
                  <span className={billingCycle === "annual" ? "active" : ""}>
                    Annual (25% Off)
                  </span>
                </div>
                <div className="cards-grid mt-5">
                  {pricingPlans.map((plan, index) => (
                    <div
                      key={index}
                      className={`pricing-card cursor-pointer ${plan.name.toLowerCase()} ${plan.badge ? "recommended" : ""
                        }`}
                      onClick={() => {
                        setFormData({ ...formData, plan: plan.name.toLowerCase() });
                        handlePlanClick(plan.name.toLowerCase(), billingCycle);
                      }}
                    >
                      {plan.badge && <span className="badge">{plan.badge}</span>}
                      <h3>{plan.name}</h3>
                      <p className="price">
                        $
                        {billingCycle === "monthly"
                          ? plan.monthlyPrice
                          : plan.annualPrice}
                        /mo
                      </p>
                      <p className="annual-text">
                        {billingCycle === "monthly"
                          ? `$${plan.annualPrice}/month with annual billing (25% off)`
                          : `Billed annually at $${plan.annualPrice * 12}`}
                      </p>
                      <button className="select-plan">
                        <span>Select Plan</span>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="pricing-buttons">
                <button className="skip" onClick={() => {
                  setFormData({ ...formData, plan: 'trial' });
                  handlePlanClick('trial', '')
                }}>
                  Start 14 Day Trial
                </button>
              </div>
            </div>
          ) : (<>
            <div className="flex items-center justify-center gap-3 p-4 rounded-lg shadow-md text-white w-full h-[100vh]">
              <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              <span>Signing up... Please wait.</span>
            </div>
          </>)}

          {step === "integration" && (
            <div className="integration-page">
              <h2>Let’s Get Started</h2>
              <p>Connect Financial or PM Software.</p>
              <div className="integration-box">
                <button onClick={() => alert("Sign in to QuickBooks")}>
                  Sign in to QuickBooks
                </button>
                <button onClick={() => alert("Sign in to Salesforce")}>
                  Sign in to Salesforce
                </button>
                <p>Connected!</p>
                <button className="skip" onClick={() => setStep("pricing")}>
                  Set Up Later
                </button>
              </div>
              <button className="skip" onClick={handleIntegrationNext}>
                Next
              </button>
            </div>
          )}

          {step === "profile" && (
            <div className="profile-page">
              <h2>Let’s Get to Know You Better</h2>
              <p>For sharper insights, tell us about your business.</p>
              <form className="profile-form" onSubmit={handleFinish}>
                <div className="form-group">
                  <input
                    type="text"
                    name="name"
                    value={companyFormData.name}
                    placeholder="Company Name"
                    onChange={handleCompanyInputChange}
                    required />
                  {companyErrors.name && (
                    <span className="error">{companyErrors.name}</span>
                  )}
                </div>
                <div className="form-group">
                  <IndustrySelector
                    value={companyFormData.industry}
                    onChange={handleIndustryChange}
                    error={companyErrors.industry}
                    industryOptions={industryOptions}
                  />
                  {/* <select 
                  name="industry"
                  value={companyFormData.industry}
                  onChange={handleCompanyInputChange} 
                  required>
                    <option value="" className="form-options !text-black">Main Industry</option>
                    {industryOptions.map((industry) => (
                      <option key={industry} value={industry.toLowerCase().replace(/ & /g, "-")} className="form-options !text-black">
                        {industry}
                      </option>
                    ))}
                  </select> */}
                  {/* {companyErrors.industry && (
                    <span className="error">{companyErrors.industry}</span>
                  )} */}
                </div>
                <div className="form-group">
                  <select
                    name="sector"
                    value={companyFormData.sector}
                    onChange={handleCompanyInputChange}
                    required>
                    <option value="" className="form-options !text-black">Sector</option>
                    <option value="saas" className="form-options !text-black">SaaS</option>
                    <option value="ecommerce" className="form-options !text-black">E-commerce</option>
                    <option value="services" className="form-options !text-black">Services</option>
                  </select>
                  {companyErrors.sector && (
                    <span className="error">{companyErrors.sector}</span>
                  )}
                </div>
                <button type="submit">Next</button>
              </form>
            </div>
          )}
        </div>
        <footer>
          <div className="footer-links">
            <a href="/pricing">Pricing</a> | <a href="/features">Features</a> |{" "}
            <a href="/contact">Contact</a>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default NewSignUp;
